'use client';

import { createContext, useCallback, useContext, useState, ReactNode } from 'react';

interface Session {
  session_id: string;
  created_at: string;
  session_title?: string;
  source?: string;
}

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SessionContextType {
  sessions: Session[];
  sessionStatus: SessionStatus;
  setSessions: (sessions: Session[]) => void;
  setSessionStatus: (status: SessionStatus) => void;
  addSession: (session: Session) => void;
  pendingSessionId: string | null;
  beginSessionNavigation: (sessionId: string) => void;
  endSessionNavigation: (sessionId: string) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('idle');
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  const addSession = (newSession: Session) => {
    setSessions((prev) => [newSession, ...prev]); // Add to front (newest first)
  };

  const beginSessionNavigation = useCallback((sessionId: string) => {
    setPendingSessionId(sessionId);
  }, []);

  const endSessionNavigation = useCallback((sessionId: string) => {
    setPendingSessionId((currentSessionId) => (currentSessionId === sessionId ? null : currentSessionId));
  }, []);

  return (
    <SessionContext.Provider
      value={{
        sessions,
        sessionStatus,
        setSessions,
        setSessionStatus,
        addSession,
        pendingSessionId,
        beginSessionNavigation,
        endSessionNavigation,
      }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within SessionProvider');
  }
  return context;
}
